/**
 * useEntitlements Hook - Static entitlements from backend (2025-11-28)
 *
 * Returns tier, visualLayer, characterLimit, quotaPolicy.
 * For dynamic usage (used, remaining, resetAt), see useQuota.
 * Replaces: useCharacterLimit, useTier.dailyLimit (use quotaPolicy.limit)
 *
 * Wire format is the `EntitlementsDto` contract from `@dramadetector/shared`.
 * Responses are validated via `fetchJsonValidated`; DB/RPC renames on the
 * backend surface as loud schema errors instead of silent zero-fallbacks.
 */
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react-native';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSearchParams } from 'expo-router';
import { getDeviceId } from '../lib/device';
import { logNetworkError } from '../lib/logNetworkError';
import { fetchJsonValidated, useAuthReady } from '@dramadetector/api-client';
import { EntitlementsDto, type Entitlements } from '@dramadetector/shared';
import {
  hasFeatureBoolean,
  getLimit as getManifestLimit,
  type Feature,
  type Limit,
  type TierId as ManifestTierId,
} from '@dramadetector/shared/entitlements';
import { getEdgeFunctionUrl } from '../lib/api-url';
import type { CanonicalTier, VisualLayer, TierSource, QuotaPolicy, UseEntitlementsReturn } from './entitlements.types';

// Re-export all types for consumers (EntitlementsData is now `Entitlements` from shared DTO).
export type { CanonicalTier, VisualLayer, TierSource, QuotaPolicy, UseEntitlementsReturn };
export type EntitlementsData = Entitlements;

// Defaults for initial/anonymous state
const DEFAULT_ENTITLEMENTS: EntitlementsData = {
  tier: 'anonymous',
  visualLayer: 'layer1',
  source: 'anonymous',
  familyPlanId: null,
  isFamilyAdmin: false,
  characterLimit: 500,
  quotaPolicy: {
    scope: 'device',
    scopeId: '',
    window: 'weekly',
    limit: 10,
  },
  updatedAt: new Date(0).toISOString(),
};

// Tier -> tier level for visual identity
function getTierLevel(tier: CanonicalTier): 1 | 2 | 3 {
  switch (tier) {
    case 'dramax':
      return 3;
    case 'drama_plus':
      return 2;
    default:
      return 1;
  }
}

/** Build synthetic entitlements for dev/test overrides (Detox + URL ?tier= param). */
function buildSyntheticEntitlementsReturn(
  tier: CanonicalTier,
  scopeId: string,
  quotaLimitOverride?: number,
): UseEntitlementsReturn {
  const tierLevel = getTierLevel(tier);
  const visualLayer: VisualLayer =
    tier === 'dramax' ? 'layer3' : tier === 'drama_plus' ? 'layer2' : 'layer1';
  const characterLimit =
    tier === 'dramax' ? 5000 : tier === 'drama_plus' ? 2000 : 1000;
  const quotaLimit =
    quotaLimitOverride ?? (tier === 'dramax' ? 200 : tier === 'drama_plus' ? 100 : 10);
  const manifestTier = tier as ManifestTierId;
  return {
    tier,
    visualLayer,
    source: 'individual',
    familyPlanId: null,
    isFamilyAdmin: false,
    tierLevel,
    isLayer1: visualLayer === 'layer1',
    isLayer2: visualLayer === 'layer2',
    isLayer3: visualLayer === 'layer3',
    isPremium: tier === 'drama_plus' || tier === 'dramax',
    characterLimit,
    quotaPolicy: { scope: 'user', scopeId, window: 'daily', limit: quotaLimit },
    isLoading: false,
    error: null,
    refetch: () => {},
    hasFeature: (feature: Feature) => hasFeatureBoolean(manifestTier, feature),
    getLimit: (limit: Limit) => getManifestLimit(manifestTier, limit),
  };
}

/** Free-tier fallback for RPC failure or missing data. */
function makeIndividualEntitlements(userId: string): EntitlementsData {
  return {
    tier: 'free',
    visualLayer: 'layer1',
    source: 'individual',
    familyPlanId: null,
    isFamilyAdmin: false,
    characterLimit: 1000,
    quotaPolicy: {
      scope: 'user',
      scopeId: userId,
      window: 'daily',
      limit: 10,
    },
    updatedAt: new Date().toISOString(),
  };
}

// Query key factory
export const entitlementsKeys = {
  all: ['entitlements'] as const,
  user: (userId: string | null) => [...entitlementsKeys.all, 'user', userId] as const,
  device: (deviceId: string) => [...entitlementsKeys.all, 'device', deviceId] as const,
};

/** Fetch static entitlements via get-entitlements CF Worker. For usage, see useQuota(). */
export function useEntitlements(): UseEntitlementsReturn {
  const { user, accessToken, loading: authLoading, isRealUser } = useAuth();
  const params = useGlobalSearchParams();

  // URL ?tier= override for dev/staging
  const allowTierOverride =
    __DEV__ || process.env.EXPO_PUBLIC_ALLOW_TIER_OVERRIDE === 'true';
  const tierOverrideParam = allowTierOverride ? (params.tier as VisualLayer) : undefined;
  const hasTierOverride =
    !!tierOverrideParam && ['layer1', 'layer2', 'layer3'].includes(tierOverrideParam);

  // Phase 2 (NocoDB #3869 close-out): use `isRealUser` from AuthContext
  // instead of re-deriving locally. Collapses the `!!accessToken && !user?.isAnonymous`
  // pattern to a single authoritative source. See AuthContextType docs.
  const isAuthenticated = isRealUser;
  const userId = user?.id || null;
  const authReady = useAuthReady();
  const isAuthReady = !isAuthenticated || authReady;

  // useQuery called unconditionally (Rules of Hooks); enabled flag gates actual fetching.
  const {
    data: entitlementsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: entitlementsKeys.user(userId),
    queryFn: async (): Promise<EntitlementsData> => {
      Sentry.addBreadcrumb({ category: 'auth.entitlements', message: 'Entitlements fetch started', level: 'info', data: { isAuthenticated, scopeExpected: isAuthenticated ? 'user' : 'device' } });
      try {
        // Phase 2 (NocoDB #3844 follow-up): drop `skipAuth: true` for anon
        // callers. skipAuth also suppresses X-Device-Id, which after Phase 1's
        // IP-fallback removal is the ONLY identity an anon request can carry
        // before the JWT arrives. Letting authenticatedFetch's default path
        // run attaches Bearer (if present) or X-Device-Id (if anonymous).
        const entitlements = await fetchJsonValidated(
          EntitlementsDto,
          getEdgeFunctionUrl('get-entitlements'),
        );

        if (__DEV__) {
          console.log('[useEntitlements] Fetched:', {
            tier: entitlements.tier,
            source: entitlements.source,
            quotaScope: entitlements.quotaPolicy.scope,
            quotaScopeId: entitlements.quotaPolicy.scopeId ? 'exists' : 'EMPTY',
            quotaLimit: entitlements.quotaPolicy.limit,
          });
        }

        // Fallback: populate scopeId when backend left it empty.
        // Phase 2 (NocoDB #3844d): the `!isAuthenticated` guard was only correct
        // pre-Phase-2 when anon users had no JWT. Post-Phase-2 the anon BA user
        // HAS a JWT (isAuthenticated=true) but the server still returns an
        // empty scopeId for `tier === 'anonymous'`. Without the fallback, the
        // empty scopeId propagates to useQuota's `isQueryEnabled` guard and
        // silently disables the quota query — homepage shows no quota for
        // anon users. Drop the guard and let any empty scopeId fall back to
        // the local device id, which authenticatedFetch still sends via the
        // X-Device-Id header path when the Bearer isn't sufficient.
        if (!entitlements.quotaPolicy.scopeId) {
          const deviceId = await getDeviceId();
          return {
            ...entitlements,
            quotaPolicy: { ...entitlements.quotaPolicy, scopeId: deviceId },
          };
        }

        return entitlements;
      } catch (err) {
        logNetworkError(err as Error, {
          hook: 'useEntitlements',
          endpoint: 'get-entitlements',
          isAuthenticated,
        });
        Sentry.addBreadcrumb({ category: 'auth.entitlements', message: 'Entitlements fetch error', level: 'error', data: { error: String(err), isAuthenticated } });
        if (isAuthenticated && userId) {
          console.warn('[useEntitlements] Fetch failed, defaulting to free:', err);
          return makeIndividualEntitlements(userId);
        }
        // Anonymous fallback: seed the scopeId with the local device ID.
        const deviceId = await getDeviceId();
        return {
          ...DEFAULT_ENTITLEMENTS,
          quotaPolicy: { ...DEFAULT_ENTITLEMENTS.quotaPolicy, scopeId: deviceId },
        };
      }
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    // Gate: wait for auth to settle; skip when tier override is active
    enabled: !authLoading && !hasTierOverride && isAuthReady,
    refetchOnMount: 'always',
  });

  // Realtime removed (2026-04-02): TODO (#2512) replace with polling/WebSocket if needed.

  // Detox E2E override (after all hooks for Rules of Hooks compliance)
  if (__DEV__) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getDetoxEntitlementsOverride } = require('../lib/dev/hook-overrides');
    const detoxResult = getDetoxEntitlementsOverride(buildSyntheticEntitlementsReturn);
    if (detoxResult) return detoxResult;
  }

  // Development/Staging override: return synthetic entitlements when ?tier= param is set
  if (hasTierOverride) {
    const tierOverride = tierOverrideParam as VisualLayer;
    const overrideTier: CanonicalTier =
      tierOverride === 'layer3'
        ? 'dramax'
        : tierOverride === 'layer2'
          ? 'drama_plus'
          : 'free';
    return buildSyntheticEntitlementsReturn(overrideTier, user?.id || '');
  }

  // Use fetched data or defaults
  const current = entitlementsData ?? DEFAULT_ENTITLEMENTS;
  const tierLevel = getTierLevel(current.tier);
  const manifestTier = current.tier as ManifestTierId;

  return {
    tier: current.tier,
    visualLayer: current.visualLayer,
    source: current.source,
    familyPlanId: current.familyPlanId,
    isFamilyAdmin: current.isFamilyAdmin,
    tierLevel,
    isLayer1: current.visualLayer === 'layer1',
    isLayer2: current.visualLayer === 'layer2',
    isLayer3: current.visualLayer === 'layer3',
    isPremium: current.tier === 'drama_plus' || current.tier === 'dramax',
    characterLimit: current.characterLimit,
    quotaPolicy: current.quotaPolicy,
    isLoading: authLoading || isLoading,
    error: error as Error | null,
    refetch,
    // Manifest-backed feature/limit checks (C3B — NocoDB #4085).
    // Sourced from `ENTITLEMENTS_MANIFEST` in `@dramadetector/shared/entitlements`.
    // Static lookups — no async / network. Use these for tier-gating UI; the
    // matching server-side gate is `enforceCapability(...)`.
    hasFeature: (feature: Feature) => hasFeatureBoolean(manifestTier, feature),
    getLimit: (limit: Limit) => getManifestLimit(manifestTier, limit),
  };
}
